/* tslint:disable:no-else-after-return */
import * as analytic from '@anm/analytic/playerAnalytic';
import {analyticsEventUrl, getViewer, isDev, isProd, sendData, sendDataUrl} from '@anm/analytic/playerAnalytic/helpers';
import { SeekEvent } from '../../../types';
import partition from 'lodash/partition';
import Plyr from 'plyr';
import { useEffect, useRef } from 'react';

type RegisteredEvent = Extract<
  Plyr.StandardEvent | Plyr.Html5Event,
  'timeupdate' | 'playing' | 'pause' | 'seeked' | 'error' | 'ended' | 'loadedmetadata'
>;

// type EventCallback = (e: Plyr.PlyrEvent) => void;
type EventCallback = (e: Plyr.PlyrEvent | Event) => boolean;

type BackendEvents = string

const registerEvents = (player: Plyr, events: Record<RegisteredEvent, EventCallback>) => {

  // todo consider also beforeunload
  // todo use pagehide on mobile
  window.addEventListener('unload', events.ended, false);

  Object.keys(events).forEach((key: RegisteredEvent) => player.on(key, events[key]));

  return () => Object.keys(events).forEach((key: RegisteredEvent) => player.off(key, events[key]));
};

const useAnalytic = (projectId: string, player: any | null) => {
  const viewId = useRef(analytic.generateViewId()).current;

  useEffect(() => {
    if(!viewId) return;

    window.parent.postMessage({viewId}, '*')
  }, [viewId])

  useEffect(() => {
    if (!player || !viewId || isDev) return; // TODO: remove isDev from condition if analytic should be anabled in dev mode

    const eventQueue: analytic.EventAction[] = [];

    const secondsAlreadyTracked: number[] = []
    let thresholds = [25, 50, 75, 100]
    let secondsWatched = 0;

    let startTimestamp = 0;
    let lastTime = 0;
    let started = false;
    let seeking = false;
    let seekStart = 0;
    let allowUpdating = true;
    let endSent = false;

    // testing
    if (!isProd) {
      window.addEventListener('unload', () => {
        const canSendEndEvent = !endSent && player.playing;

        canSendEndEvent && sendDataUrl(analyticsEventUrl)({event: 'unload', viewId, projectId})
      }, false);
      window.addEventListener('beforeunload', () => {
        sendDataUrl(analyticsEventUrl)({event: 'beforeunload', viewId, projectId})
      }, false);
      window.addEventListener('pagehide', () => {
        sendDataUrl(analyticsEventUrl)({event: 'pagehide', viewId, projectId})
      }, false);
      window.addEventListener('pageshow', () => {
        sendDataUrl(analyticsEventUrl)({event: 'pageshow', viewId, projectId})
      }, false);
      window.addEventListener('visibilitychange', () => {
        sendDataUrl(analyticsEventUrl)({event: 'visibilitychange', visibilityState: document.visibilityState, viewId, projectId})
      }, false);
    }

    const getDeltaTime = () => Date.now() - startTimestamp;

    const processQueue = () => {
      sendData([{
        viewId,
        projectId,
        viewerId: getViewer(),
        mediaDuration: player.duration,
        autoplay: player.autoplay,
        currentClientTime: Date.now(),
        href: location.href,
        referrer: document.referrer || '',
        width: window.innerWidth,
        events: eventQueue.splice(0, eventQueue.length)
      }])
    };

    player.onseek = (evt: SeekEvent) => {
      if (allowUpdating && Math.abs(evt.currentTime - seekStart) > 1) {
        seekStart = evt.currentTime;
      }
    };

    const eventBasic = (eventName: BackendEvents, extra: {} = {}, skipWatched: boolean = false): EventCallback => e => {
      // console.log('server', getCurrentMillis());
      const watched = skipWatched
        ? null
        : Math.max(0, eventName === 'seek' ? (seekStart - lastTime) : (player.currentTime - lastTime));

      const event: analytic.EventAction = {
        eventName,
        deltaTime: getDeltaTime(),
        eventTime: player.currentTime,
        eventTimestamp: Date.now(),
        watched,
        ...extra
      };
      if (!skipWatched) lastTime = player.currentTime;
      endSent = eventName === 'end';

      console.info(`eventName=${eventName} eventTime=${event.eventTime} watched=${event.watched} deltaTime=${event.deltaTime} lastTime=${lastTime} seekStart=${seekStart} seeking=${player?.seeking} ss=${seeking} pause=${player?.paused} playing=${player?.playing} extra=${JSON.stringify(extra)}`, e);
      eventQueue.push(event);
      processQueue(); // for now we send it for each event
      return true;
    }

    const onUpdate: EventCallback = e => {
      // console.warn(c, seekStart, e);
      const d = player.duration
      const c = player.currentTime
      const cc = Math.floor(c)
      const p = Math.round(c) / Math.round(d) * 100;

      const [toReport, _thresholds] = partition(thresholds, t => p >= t)
      thresholds = _thresholds;
      toReport.forEach(() => eventBasic('percentWatched', { percentWatched: p }, true)(e));

      if (!secondsAlreadyTracked.includes(cc)) {
        secondsAlreadyTracked.push(cc);
        secondsWatched += 1;
        if (secondsWatched % 3 === 0) {
          eventBasic('update')(e)
          return true;
        }
      }
      return false;
    }

    // todo add `ratechange`
    return registerEvents(player, {
      pause: e => {
        if (player.currentTime === player.duration) return false;
        else if (player.seeking) {
          seeking = player.seeking;
          return false;
        } else return eventBasic('pause')(e);
      },
      seeked: eventBasic('seek'),
      ended: e => {
        const canSendEndEvent = !endSent && (player.ended || player.playing);

        return canSendEndEvent ? eventBasic('end')(e) : false
      },
      error: eventBasic('error'),
      timeupdate: onUpdate,
      playing: e => {
        const n = started ? 'unpause' : 'start';
        started = true;
        if (n === 'unpause' && seeking) {
          allowUpdating = true;
          seeking = false;
          return false;
        } else {
          return eventBasic(n)(e);
        }
      },
      loadedmetadata: e => {
        startTimestamp = Date.now();
        return eventBasic('load', { deltaTime: 0 })(e);
      }
    });

  }, [player, viewId]);
};

export default useAnalytic;